import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Sparkles, X, Minus, ChevronUp, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { classNames } from "../lib/utils";
import AIChatHistory from "./ai/AIChatHistory";
import AIChatComposer from "./ai/AIChatComposer";
import { useAIPanelSize } from "./ai/useAIPanelSize";

/**
 * Floating AI assistant.
 *
 * Behavior:
 *   - Bottom-right button visible on every page (the parent decides when not to mount).
 *   - Clicking opens a slide-up panel with a chat scroll + input.
 *   - Maintains a session_id across messages so multi-turn context is preserved.
 *   - Uses /api/ai/assistant (permission-scoped server-side).
 *   - Reads /api/ai/quota at mount and after each call to display X/20 remaining.
 */
export default function AIAssistantWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quota, setQuota] = useState({ used: 0, limit: 20, remaining: 20 });
  const { size, fullscreen, setFullscreen, minimized, setMinimized, onResizeStart } = useAIPanelSize();
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const onR = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  const refreshQuota = useCallback(() => {
    api.get("/ai/quota").then(({ data }) => setQuota(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) refreshQuota();
  }, [open, refreshQuota]);

  if (!user) return null;

  const send = async () => {
    const q = (input || "").trim();
    if (!q || pending) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setPending(true);
    try {
      const { data } = await api.post("/ai/assistant", { question: q, session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
      refreshQuota();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || "AI daily limit reached. Try again tomorrow.");
        setMessages((m) => [...m, { role: "system", text: "AI daily limit reached." }]);
      } else if (status === 402) {
        toast.error(detail || "AI key budget exceeded.");
        setMessages((m) => [...m, { role: "system", text: detail || "AI budget exceeded." }]);
      } else {
        toast.error(detail || "AI request failed.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-2xl px-4 py-3 text-sm font-semibold transition-transform hover:scale-105 lg:bottom-6 lg:right-44 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
          data-testid="ai-assistant-fab"
          aria-label="Ask NestVillage AI"
        >
          <Sparkles size={16} />
          <span className="hidden sm:inline">Ask NestVillage AI</span>
          <span className="sm:hidden">AI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className={classNames(
            "fixed z-50 inset-x-0 bottom-0 lg:inset-auto lg:bottom-6 lg:right-6",
            fullscreen && "lg:!inset-4 lg:!bottom-4 lg:!right-4",
          )}
          data-testid="ai-assistant-panel"
          data-fullscreen={fullscreen}
          data-minimized={minimized}
        >
          <div
            className={classNames(
              "relative bg-white border border-slate-200 lg:rounded-2xl shadow-2xl w-full flex flex-col overflow-hidden transition-[height] duration-150",
              minimized ? "h-14" : "h-[78vh]",
              fullscreen ? "lg:!w-full lg:!h-full" : "",
            )}
            style={!fullscreen && isDesktop ? { width: size.w, height: minimized ? 56 : size.h } : undefined}
            data-testid="ai-assistant-window"
          >
            {!fullscreen && !minimized && (
              <button
                type="button"
                onMouseDown={onResizeStart}
                className="hidden lg:block absolute top-0 left-0 w-5 h-5 cursor-nwse-resize z-10 rounded-tl-2xl bg-white/10 hover:bg-white/40"
                aria-label="Resize chat window (drag)"
                title="Drag to resize"
                data-testid="ai-resize-handle"
              />
            )}
            <div className="px-4 py-2.5 bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles size={17} />
                <p className="font-heading font-bold text-[15px]">NestVillage AI</p>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] bg-white/20 rounded-full px-2 py-0.5 mr-1" data-testid="ai-quota-pill">
                  {quota.remaining}/{quota.limit} today
                </span>
                <button onClick={() => setMinimized((m) => !m)} className="p-1.5 rounded hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70" aria-label={minimized ? "Restore chat" : "Minimize chat"} title={minimized ? "Restore" : "Minimize"} data-testid="ai-assistant-minimize-btn">
                  {minimized ? <ChevronUp size={17} /> : <Minus size={17} />}
                </button>
                <button onClick={() => { setFullscreen((f) => !f); setMinimized(false); }} className="hidden lg:inline-flex p-1.5 rounded hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70" aria-label={fullscreen ? "Exit fullscreen" : "Expand to fullscreen"} title={fullscreen ? "Exit fullscreen" : "Fullscreen"} data-testid="ai-assistant-fullscreen-btn">
                  {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button
                  onClick={() => { setOpen(false); setFullscreen(false); setMinimized(false); }}
                  className="p-1.5 rounded hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                  aria-label="Close"
                  title="Close"
                  data-testid="ai-assistant-close-btn"
                >
                  <X size={17} />
                </button>
              </div>
            </div>

            {!minimized && (
              <>
                <AIChatHistory messages={messages} pending={pending} onSuggest={setInput} />
                <AIChatComposer value={input} onChange={setInput} onSend={send} pending={pending} />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
